import { useState } from 'react'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { siteConfig } from '@/data/content'
import { supabase } from '@/lib/supabase'
import { trackGenerateLead } from '@/lib/analytics'

type Origin = 'producto' | 'calculadora' | 'contacto' | 'general'

const REF_PREFIX: Record<Origin, string> = {
  producto:    'PROD',
  calculadora: 'CALC',
  contacto:    'CONT',
  general:     'WEB',
}

interface WhatsAppDialogProps {
  productName?: string
  summary?: string
  origin?: Origin
  triggerNode?: React.ReactNode
}

function generateRef(origin: Origin = 'general'): string {
  return `${REF_PREFIX[origin]}-${Math.floor(1000 + Math.random() * 9000)}`
}

const MAX_CHARS = 400

export function WhatsAppDialog({ productName, summary, origin = 'general', triggerNode }: WhatsAppDialogProps) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [refId, setRefId] = useState(() => generateRef(origin))

  const charsLeft = MAX_CHARS - message.length
  const canSend = message.trim().length > 0 || !!summary

  const dialogTitle = productName
    ? <> Consultando sobre <span className="text-forest-500">{productName}</span></>
    : summary
      ? 'Solicitar cotización'
      : 'Envianos tu consulta'

  const handleSend = () => {
    const userMsg = message.trim()
    const footer = `udgarden.uy · ${refId}`
    const parts = [userMsg || null, summary || null, footer].filter(Boolean)
    const text = parts.join('\n')

    supabase.from('consultas').insert({
      ref:        refId,
      origen:     origin,
      producto:   productName ?? null,
      resumen:    summary     ?? null,
      mensaje:    message.trim() || null,
      url_origen: window.location.href,
      estado:     'abierta',
    }).then(({ error }) => {
      if (!error) {
        supabase.functions.invoke('notify-new-consulta', { body: { ref: refId } }).catch(() => {})
      }
    })

    trackGenerateLead(origin, refId)

    window.open(`${siteConfig.whatsapp}?text=${encodeURIComponent(text)}`, '_blank')
    setOpen(false)
    setMessage('')
    setRefId(generateRef(origin))
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerNode ?? (
          <button className="inline-flex items-center gap-2 bg-forest-800 hover:bg-forest-700 text-white text-xs font-medium uppercase tracking-widest px-6 py-2.5 rounded-full transition-all duration-200">
            Consultar por WhatsApp
          </button>
        )}
      </DialogTrigger>

      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="
          bg-white p-0 gap-0 ring-0 border border-forest-100 shadow-overlay
          sm:max-w-[440px] sm:rounded-2xl
          max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:right-0
          max-sm:translate-x-0 max-sm:translate-y-0
          max-sm:max-w-none max-sm:w-full
          max-sm:rounded-t-3xl max-sm:rounded-b-none
          max-sm:data-open:slide-in-from-bottom-4 max-sm:data-open:zoom-in-100
          max-sm:data-closed:slide-out-to-bottom-4 max-sm:data-closed:zoom-out-100
        "
      >
        <DialogTitle className="sr-only">Consulta por WhatsApp</DialogTitle>

        {/* Drag handle — solo mobile */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-9 h-1 rounded-full bg-bone-200" />
        </div>

        {/* Header */}
        <div className="px-6 pt-4 pb-4 sm:pt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2.5">
                <span className="w-2 h-2 rounded-full bg-[#25D366] shadow-[0_0_0_3px_#25D36622]" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-bone-400">WhatsApp</span>
              </div>
              <p className="text-base font-medium text-forest-800 leading-snug">{dialogTitle}</p>
            </div>
            <DialogClose className="mt-0.5 p-1.5 rounded-full text-bone-300 hover:text-forest-800 hover:bg-bone-100 transition-colors shrink-0">
              <X className="w-4 h-4" />
            </DialogClose>
          </div>
        </div>

        {/* Summary — datos de calculadora (read-only) */}
        {summary && (
          <div className="mx-6 mb-3 flex items-start gap-2.5 bg-forest-50 border border-forest-100 rounded-xl px-4 py-3">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-forest-400 shrink-0 mt-0.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="9" y="2" width="6" height="4" rx="1"/><path d="M3 6h18M8 6v14M16 6v14M3 6l1 14h16l1-14"/>
            </svg>
            <p className="text-xs text-forest-700 leading-relaxed">{summary}</p>
          </div>
        )}

        {/* Textarea */}
        <div className="mx-6 mb-4 rounded-xl border border-forest-100 bg-bone-50 px-4 pt-3 pb-2">
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, MAX_CHARS))}
            placeholder={summary ? 'Agregá una consulta o comentario (opcional)...' : 'Escribí tu consulta acá...'}
            rows={4}
            autoFocus
            className="w-full bg-transparent text-sm text-forest-800
                       placeholder:text-bone-400 resize-none
                       focus:outline-none leading-relaxed"
          />
          <div className="flex justify-end pt-1">
            <span className={`text-[10px] tabular-nums transition-colors ${charsLeft < 50 ? 'text-terracotta-500' : 'text-bone-300'}`}>
              {charsLeft}
            </span>
          </div>
        </div>

        {/* Aviso */}
        <p className="px-6 pb-3 text-[10px] text-bone-400 leading-relaxed">
          Al abrir WhatsApp, enviá el mensaje tal como está para que podamos identificar tu consulta.
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 pb-5">
          <span className="font-mono text-[10px] text-bone-300 select-none">{refId}</span>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className="flex items-center gap-2 bg-forest-800 hover:bg-forest-700
                       disabled:opacity-30 disabled:cursor-not-allowed
                       text-white text-[11px] font-medium uppercase tracking-widest
                       px-5 py-2.5 rounded-full transition-all duration-200 active:scale-95"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current shrink-0" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.554 4.121 1.523 5.854L.057 23.882l6.174-1.621A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.808 9.808 0 01-5.001-1.368l-.359-.213-3.664.962.979-3.575-.234-.369A9.818 9.818 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182c5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/>
            </svg>
            Enviar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
